import { useEffect, useState } from 'react';
import { Button } from '@/components/shadcn/button';
import { Input } from '@/components/shadcn/input';
import { Label } from '@/components/shadcn/label';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from '@/components/shadcn/sheet';
import { pointPickerValues } from './badmintonScoringUtils';

interface BadmintonPointPickerSheetProps {
  open: boolean;
  side: 'a' | 'b' | null;
  currentValue: number;
  scoringType: string;
  onClose: () => void;
  onSelect: (value: number) => void;
}

export default function BadmintonPointPickerSheet({
  open,
  side,
  currentValue,
  scoringType,
  onClose,
  onSelect,
}: BadmintonPointPickerSheetProps) {
  const presets = pointPickerValues(scoringType);
  const inGrid = presets.includes(currentValue);
  const [showCustom, setShowCustom] = useState(!inGrid);
  const [customValue, setCustomValue] = useState(inGrid ? '0' : String(currentValue));

  useEffect(() => {
    if (!open) return;
    const listed = pointPickerValues(scoringType).includes(currentValue);
    setShowCustom(!listed);
    setCustomValue(listed ? '0' : String(currentValue));
  }, [open, currentValue, scoringType]);

  const submitCustom = () => {
    onSelect(Number.parseInt(customValue, 10) || 0);
  };

  return (
    <Sheet open={open} onOpenChange={(next) => !next && onClose()}>
      <SheetContent side="bottom" showCloseButton={false} className="rounded-t-3xl px-5 pb-6 pt-3">
        <div className="mx-auto mb-3 h-1 w-10 rounded-full bg-gray-200" />
        <SheetHeader className="space-y-1 text-left">
          <SheetTitle className="text-lg font-extrabold text-[#0E1726]">Pilih Poin Akhir</SheetTitle>
          <SheetDescription className="text-sm text-gray-400">
            {side === 'b' ? 'Sisi kanan' : 'Sisi kiri'} · poin akhir set ini
          </SheetDescription>
        </SheetHeader>

        <div className="mt-4 grid grid-cols-4 gap-2">
          {presets.map((value) => {
            const selected = !showCustom && currentValue === value;
            return (
              <Button
                key={value}
                type="button"
                variant="outline"
                onClick={() => onSelect(value)}
                className={`h-14 rounded-2xl text-base font-extrabold ${
                  selected
                    ? 'border-[#FC480D] bg-white text-[#FC480D]'
                    : 'border-gray-200 text-[#0E1726]'
                }`}
              >
                {value}
              </Button>
            );
          })}
        </div>

        <Button
          type="button"
          variant="secondary"
          onClick={() => setShowCustom(true)}
          className={`mt-3 h-12 w-full rounded-2xl text-sm font-semibold ${
            showCustom ? 'bg-[#FFF8F5] text-[#FC480D] hover:bg-[#FFF1EC]' : 'bg-gray-50 text-gray-500'
          }`}
        >
          Lainnya
        </Button>

        {showCustom && (
          <div className="mt-3 flex items-center gap-2">
            <div className="flex h-12 min-w-0 flex-1 items-center rounded-full border border-gray-200 bg-white px-4">
              <Label htmlFor="poin-lain" className="shrink-0 text-sm font-medium text-gray-400">
                Poin lain
              </Label>
              <Input
                id="poin-lain"
                type="text"
                inputMode="numeric"
                value={customValue}
                onChange={(e) => setCustomValue(e.target.value.replace(/[^\d]/g, ''))}
                className="h-11 border-0 bg-transparent px-2 text-right shadow-none focus-visible:border-transparent"
              />
            </div>
            <Button
              type="button"
              onClick={submitCustom}
              className="h-12 rounded-full bg-[#FC480D] px-6 font-bold text-white hover:bg-[#D83B08]"
            >
              Simpan
            </Button>
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
}
